import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { ExternalLink, AlertTriangle } from "lucide-react";

const DISMISS_KEY = "cm_inapp_browser_dismissed";

const getCookie = (name) => {
  const match = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
  return match ? decodeURIComponent(match[2]) : null;
};

const setCookie = (name, value, days = 90) => {
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(value)};expires=${expires};path=/;SameSite=Lax`;
};

/**
 * Detects Facebook Messenger, Instagram, and other social in-app browsers
 * that have known issues with localStorage, OAuth, and file uploads.
 */
const detectInAppBrowser = () => {
  const ua = navigator.userAgent || "";
  if (/FBAN|FBAV|FB_IAB/i.test(ua)) return "Facebook";
  if (/Instagram/i.test(ua)) return "Instagram";
  if (/Snapchat/i.test(ua)) return "Snapchat";
  if (/Twitter|X-Client/i.test(ua)) return "X";
  if (/Pinterest/i.test(ua)) return "Pinterest";
  if (/Line\//i.test(ua)) return "LINE";
  if (/MicroMessenger/i.test(ua)) return "WeChat";
  // Generic: embedded webview but not a known full browser
  if (/wv\)|WebView/i.test(ua) && !/Chrome\/[\d.]+ Mobile Safari/i.test(ua)) return "in-app";
  return null;
};

function InAppBrowserModal() {
  const { t } = useTranslation();
  const [show, setShow] = useState(false);
  const [appName, setAppName] = useState("");

  useEffect(() => {
    // Already dismissed?
    const dismissed =
      localStorage.getItem(DISMISS_KEY) === "true" || getCookie(DISMISS_KEY) === "true";
    if (dismissed) return;

    const detected = detectInAppBrowser();
    if (detected) {
      setAppName(detected);
      setShow(true);
    }
  }, []);

  const handleDismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, "true"); } catch {}
    setCookie(DISMISS_KEY, "true");
    setShow(false);
  };

  const handleOpenInBrowser = () => {
    // Attempt to open in external browser — works on some platforms
    const url = window.location.href;

    // Android intent-based open (works in Facebook/Instagram on Android)
    const intentUrl = `intent://${url.replace(/^https?:\/\//, "")}#Intent;scheme=https;end`;

    // iOS: unfortunately no reliable way to force external browser,
    // so we show instructions for "Open in Safari" menu option
    const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);

    if (!isIOS) {
      window.location.href = intentUrl;
    }

    // Persist dismissal regardless
    handleDismiss();
  };

  if (!show) return null;

  const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(0, 0, 0, 0.6)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 10002,
        padding: "16px",
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="inapp-browser-title"
    >
      <div
        style={{
          background: "#fff",
          borderRadius: "16px",
          padding: "28px 24px",
          maxWidth: "420px",
          width: "100%",
          boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Icon */}
        <div
          style={{
            width: "56px",
            height: "56px",
            borderRadius: "14px",
            background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto 16px auto",
          }}
        >
          <AlertTriangle size={28} color="#fff" />
        </div>

        {/* Title */}
        <h2
          id="inapp-browser-title"
          style={{
            margin: "0 0 12px 0",
            fontSize: "18px",
            fontWeight: 700,
            color: "#1f2937",
            textAlign: "center",
          }}
        >
          {t("inAppBrowser.title", "Open in your browser for the best experience")}
        </h2>

        {/* Description */}
        <p
          style={{
            margin: "0 0 20px 0",
            fontSize: "14px",
            color: "#6b7280",
            lineHeight: 1.6,
            textAlign: "center",
          }}
        >
          {t(
            "inAppBrowser.description",
            "You're viewing this inside {{appName}}'s built-in browser. Some features like login and saved preferences may not work correctly here.",
            { appName }
          )}
        </p>

        {/* Instructions */}
        <div
          style={{
            background: "#f0f9ff",
            borderRadius: "10px",
            padding: "14px 16px",
            marginBottom: "20px",
            border: "1px solid #bae6fd",
          }}
        >
          <p
            style={{
              margin: 0,
              fontSize: "13px",
              color: "#0369a1",
              lineHeight: 1.6,
            }}
          >
            {isIOS
              ? t(
                  "inAppBrowser.instructionsIOS",
                  "Tap the ··· or share button, then choose \"Open in Safari\" to switch."
                )
              : t(
                  "inAppBrowser.instructionsAndroid",
                  "Tap the ⋮ menu in the top-right corner, then choose \"Open in Chrome\" to switch."
                )}
          </p>
        </div>

        {/* Buttons */}
        {!isIOS && (
          <button
            onClick={handleOpenInBrowser}
            style={{
              width: "100%",
              padding: "14px",
              background: "linear-gradient(135deg, #105D8E 0%, #0a4d6e 100%)",
              border: "none",
              borderRadius: "10px",
              color: "#fff",
              fontSize: "15px",
              fontWeight: 600,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              marginBottom: "10px",
              minHeight: "48px",
            }}
          >
            <ExternalLink size={18} />
            {t("inAppBrowser.openInBrowser", "Open in Browser")}
          </button>
        )}

        <button
          onClick={handleDismiss}
          style={{
            width: "100%",
            padding: "12px",
            background: "transparent",
            border: "none",
            color: "#9ca3af",
            fontSize: "14px",
            cursor: "pointer",
            minHeight: "44px",
          }}
        >
          {t("inAppBrowser.continueAnyway", "Continue anyway")}
        </button>
      </div>
    </div>
  );
}

export default InAppBrowserModal;
